<template>
  <div>
    <ExitNavBar :title="navTitle" @exit="onExit" />
    <div class="add-edit-case">
      <div class="navbar-background" id="addEditNavBar">
        <div v-if="isEditMode" class="add-edit-case-nav-bar" data-cy="case-nav-bar">
          <h3 class="page-title" data-cy="case-title">
            {{ pageTitle }}
          </h3>
          <BreadcrumbMenu class="breadcrumb-menu" :crumbs="breadcrumbItems"> </BreadcrumbMenu>
          <div class="action-buttons">
            <BackstageButton
              :disabled="!isCurrentStepActionEnabled"
              :variant="ButtonVariant.PRIMARY"
              :icon-right="submitting ? 'loading' : 'arrow-right'"
              :data-cy="`${currentStep}-button-cnt`"
              :data-cy-button="`${currentStep}-button`"
              @click.prevent="handleStepAction">
              {{ $t(`create_edit_case.execute_${currentStep}_step_button`) }}
            </BackstageButton>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loader"></div>

      <div v-else class="current-step-content">
        <ProductsModelsSelectorV3
          v-if="currentStepIsProductModelsSelection"
          :order-type="caseType"
          :order-date="caseOrderDate" />

        <ProductsQuantitiesSelectorV3 v-if="currentStepIsQuantities" />

        <CaseView
          v-if="currentStepIsOrderDetails"
          :case-id="caseId"
          :case-type="caseType"
          @case-is-valid="updateCaseValidity" />

        <NotificationMessage
          v-if="showSubmitReminderPopup"
          :title="$t('viewCase.submit_reminder_popup_title')"
          :size="PopUpSize.Small"
          :show-close-button="true"
          @close="showSubmitReminderPopup = false">
          <SubmitReminder
            :submitting="saving"
            :deleting="deleting"
            :submit-enabled="isSubmitEnabled"
            @save="onSaveCase"
            @delete="deleteCase"
            @submit="submitCase" />
        </NotificationMessage>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CaseType } from '@onrunning/backstage-models';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import BackstageButton from '@/components/common/BackstageButton.vue';
import BreadcrumbMenu from '@/components/common/breadcrumb-menu.vue';
import ExitNavBar from '@/components/common/exit-nav-bar.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import SubmitReminder from '@/components/common/submit-reminder.vue';
import ProductsModelsSelectorV3 from '@/components/matrix/products-models-selector-v2.vue';
import ProductsQuantitiesSelectorV3 from '@/components/matrix/products-quantities-selector-v3.vue';
import { useFormat } from '@/composables/format';
import { useBreadcrumbMenu } from '@/composables/useBreadcrumbMenu';
import { useCaseManagement } from '@/composables/useCaseManagement';
import { useCaseValidation } from '@/composables/useCaseValidation';
import { useNotification } from '@/composables/useNotification';
import { AddEditCaseBreadcrumbSteps } from '@/models/add-edit-case-breadcrumb-steps.enum';
import { ButtonVariant } from '@/models/button-variant.enum';
import { PopUpSize } from '@/models/pop-up-size.enum';
import { useCaseStore } from '@/stores/case';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { ReturnCase, WarrantyCase } from '@/types/case.interface';
import type { MatrixOrder } from '@/types/matrix.interface';
import type { MatrixOrderProduct } from '@/types/product.interface';
import CaseView from '@/views/CaseViewV3.vue';

const props = defineProps<{
  id?: string;
  caseType: CaseType;
}>();

const route = useRoute();
const router = useRouter();
const caseStore = useCaseStore();
const userStore = useUserStore();
const productMatrixStore = useProductMatrixStore();

const { t } = useI18n();
const { notifyError } = useNotification();
const { formatDateToIsoStringIgnoringTime } = useFormat();
const { isNewCase, isMatrixOrderValid, isEditMode } = useCaseValidation();
const { fetchCase, submitCase, deleteCase, upsertCase, navigateBackToCases, saving, deleting, submitting } =
  useCaseManagement(props);

const loading = ref(true);
const isCaseDetailsValid = ref(false);
const showSubmitReminderPopup = ref(false);

const caseId = computed(() => props.id ?? (route.params['id'] as string | undefined));

const navTitle = computed(() => {
  if (!props.caseType) return '';

  const caseNumber = caseStore.caseOrder?.caseNumber ? ` - ${caseStore.caseOrder?.caseNumber}` : '';

  return t(`create_edit_case.case_type['${props.caseType.toLowerCase()}']`).toString() + caseNumber;
});

const pageTitle = computed(() => {
  const titlePrefix = isNewCase.value ? '' : `${t('viewCase.page_title_new_prefix')} `;

  return titlePrefix + t(`viewCase.page_title_${props.caseType?.toLowerCase()}`);
});

const currentStepIsProductModelsSelection = computed(
  () => currentStep.value === AddEditCaseBreadcrumbSteps.productsModelsSelection,
);
const currentStepIsQuantities = computed(() => currentStep.value === AddEditCaseBreadcrumbSteps.productsQuantities);
const currentStepIsOrderDetails = computed(() => currentStep.value === AddEditCaseBreadcrumbSteps.details);

const isCurrentStepActionEnabled = computed((): boolean => {
  if (submitting.value) {
    return false;
  }

  switch (currentStep.value) {
    case AddEditCaseBreadcrumbSteps.productsModelsSelection:
      return productMatrixStore.selectedProductModelsNames.size > 0;
    case AddEditCaseBreadcrumbSteps.productsQuantities:
      return isMatrixOrderValid.value;
    case AddEditCaseBreadcrumbSteps.details:
      return isSubmitEnabled.value;
    default:
      return true;
  }
});

const caseOrderDate = computed(() => {
  const orderDate = caseStore.caseOrder?.createdAt ? new Date(caseStore.caseOrder.createdAt) : new Date();

  return formatDateToIsoStringIgnoringTime(orderDate);
});

const isSubmitEnabled = computed(() => {
  return !submitting.value && isCaseDetailsValid.value;
});

const breadCrumbConfig = computed(() => {
  return {
    steps: Object.values(AddEditCaseBreadcrumbSteps),
    translationNamespace: 'create_edit_case',
    routePrefix: `/${props.caseType.toLowerCase()}-cases/`,
    extraPath: props.id ? `${props.id}/` : 'create/',
  };
});

const { breadcrumbItems, currentStep } = useBreadcrumbMenu<AddEditCaseBreadcrumbSteps>(breadCrumbConfig);

onMounted(() => {
  window.addEventListener('beforeunload', onBeforeUnloadHandler);
  initCase();
});

onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnloadHandler);

  caseStore.reset();
  productMatrixStore.reset();
});

// methods
const initCase = async () => {
  loading.value = true;
  caseStore.setCaseType(props.caseType);

  try {
    if (caseId.value) {
      const caseOrder = await fetchCase(caseId.value, userStore.selectedAccount.id);

      initMatrixStore(caseOrder);
    } else {
      productMatrixStore.initMatrixOrder();
    }
  } catch (e) {
    notifyError(t('global.error'));
    navigateBackToCases();
  } finally {
    loading.value = false;
  }
};

const initMatrixStore = (caseOrder: WarrantyCase | ReturnCase) => {
  let matrixTotal = 0;
  const matrixProducts: Record<string, MatrixOrderProduct> = {};
  const productGroupNames = new Set<string>();

  for (const line of caseOrder.lines) {
    const matrixProduct: MatrixOrderProduct = {
      productName: line.productName,
      productGroupId: line.productGroupId,
      productGroupName: line.productGroupName,
      productGroupNumber: line.productNumber,
      quantity: line.quantity,
      productId: line.productId,
    };

    matrixProducts[matrixProduct.productId] = matrixProduct;
    matrixTotal += matrixProduct.quantity;

    if (line.productGroupName) {
      productGroupNames.add(line.productGroupName);
    }
  }

  const matrixOrder: MatrixOrder = {
    orderId: caseOrder.id,
    orderNumber: caseOrder.caseNumber,
    date: new Date(caseOrder.createdAt),
    matrixProducts,
    total: matrixTotal,
  };

  productMatrixStore.setMatrixOrders([matrixOrder]);
  productMatrixStore.setSelectedProductModels(productGroupNames);
};

const confirmNavigatingAway = (path: string) => {
  notifyError(t('global.warning_unsaved_changes'), {
    buttons: [
      {
        text: t('global.continue_button'),
        action: () => router.push(path),
        className: ButtonVariant.SECONDARY,
      },
    ],
  });
};

const onBeforeUnloadHandler = (event: BeforeUnloadEvent) => {
  if (productMatrixStore.matrixUpdated || caseStore.caseUpdated) {
    event.preventDefault();
  }
};

const onExit = () => {
  if (!isNewCase.value && isEditMode.value) {
    showSubmitReminderPopup.value = true;

    return;
  }

  if (productMatrixStore.matrixUpdated) {
    confirmNavigatingAway(`/${props.caseType.toLocaleLowerCase()}-cases`);

    return;
  }

  router.push(`/${props.caseType.toLocaleLowerCase()}-cases`);
};

const handleStepAction = async () => {
  switch (currentStep.value) {
    case AddEditCaseBreadcrumbSteps.productsModelsSelection:
      return router.replace({
        params: { ...route.params, step: AddEditCaseBreadcrumbSteps.productsQuantities },
      });
    case AddEditCaseBreadcrumbSteps.productsQuantities:
      onProceedToOrderDetails();
      break;
    case AddEditCaseBreadcrumbSteps.details:
      await submitCase();
      break;
  }
};

const onProceedToOrderDetails = async () => {
  try {
    await upsertCase();

    router.push({
      params: {
        ...route.params,
        step: AddEditCaseBreadcrumbSteps.details,
      },
    });
  } catch (error: any) {
    notifyError(t(error?.message) || t('backend.cases.save_case_error'));

    return;
  }
};

const onSaveCase = async () => {
  try {
    saving.value = true;

    await upsertCase();
    navigateBackToCases();
  } catch (error: any) {
    notifyError(t(error?.message) || t('backend.cases.save_case_error'));

    return;
  } finally {
    saving.value = false;
  }
};

const updateCaseValidity = (valid: boolean) => {
  isCaseDetailsValid.value = valid;
};
</script>

<style lang="scss" scoped>
.add-edit-case {
  margin-bottom: 1.25rem;

  .navbar-background {
    @include navbar-background;
  }

  .add-edit-case-nav-bar {
    @include common-container;
    background-color: var(--primary-bg-color);
    padding: 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    border-radius: 0;
    gap: 30px;

    @include breakpoint(medium) {
      padding: 10px !important;
    }

    .page-title {
      font-size: 1.25rem;
      font-weight: 400;
      text-align: left;
    }

    .action-buttons {
      min-width: 10rem;
      .hidden {
        display: none;
      }
    }
  }

  .buttons-container {
    padding: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.5625rem;
    margin-bottom: 1rem;
  }
}
</style>
